import React, { useState, useEffect, useMemo } from 'react';
import { caseFileAPI, masrafAPI } from '../../api/api';
// Turkce yazimli tutarlari ('12.000', '12.000,50') dogru okumak icin.
import { parseTutar } from '../../utils/vekaletUcreti';
import {
  hesaplaIcra,
  TAHSIL_ASAMA_LABELS,
  icraAcilisMasraflari,
  DEFAULT_MASRAF_KALEM_DAHIL,
  masrafToplamFromKalemler,
  ICRA_ACILIS_MASRAF_TURU,
  MASRAF_KALEM_SURUM,
  uyapDosyaHesabiMasraf
} from '../../utils/icraHesap';
import { bugunISO, hesaplaFaiz, getGuncelAvansOrani, isVarsayilanAvansOrani } from '../../utils/faiz';
import { formatDateForDateInput, toISODateOrNull } from '../../utils/dateUtils';
import './IcraHesap.css';

const formatCurrency = (a) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(
    Number.isFinite(a) ? a : 0
  );

// Takvim günü (YYYY-MM-DD) — UTC kayması olmadan oku/yaz
const toDateInput = (v) => formatDateForDateInput(v) || '';

const formatTR = (str) => {
  if (!str) return '-';
  // str zaten YYYY-MM-DD ise doğrudan parçala (timezone kayması olmasın)
  const only = String(str).split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(only)) {
    const [y, m, d] = only.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
  }
  const d = new Date(only + 'T00:00:00');
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const get = (o, pas, cam) => (o ? (o[pas] ?? o[cam]) : undefined);

/** Türkçe harfleri sadeleştirip büyütür — masraf türleri serbest metin girilmiş. */
const sadelestir = (metin) =>
  String(metin || '')
    .replace(/İ/g, 'I').replace(/ı/g, 'i')
    .replace(/Ş/g, 'S').replace(/ş/g, 's')
    .replace(/Ğ/g, 'G').replace(/ğ/g, 'g')
    .replace(/Ü/g, 'U').replace(/ü/g, 'u')
    .replace(/Ö/g, 'O').replace(/ö/g, 'o')
    .replace(/Ç/g, 'C').replace(/ç/g, 'c')
    .toUpperCase().trim();

/**
 * Bir masraf kaydı "icra takip açılış" masrafı mı?
 *
 * Masraf türü serbest metin olduğu için sistemde birden çok yazım var:
 * "İCRA TAKİP AÇILIŞ HARCI", "İCRA AÇILIŞ MASRAFLARI", "İCRA AÇILIŞ MASRAFI"...
 * Bu yüzden tam eşitlik değil, "İCRA" + "AÇILIŞ" birlikteliği aranıyor.
 */
const acilisMasrafiMi = (masrafTuru) => {
  const t = sadelestir(masrafTuru);
  return t.includes('ICRA') && t.includes('ACILIS');
};
const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : (Number(v) || 0));
const parseId = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const apiHataMesaji = (e) => {
  const d = e?.response?.data;
  if (typeof d === 'string' && d.trim()) return d;
  if (d?.message) return d.message;
  if (d?.title) return d.title;
  if (e?.message) return e.message;
  return 'Kaydedilemedi';
};

const SegmentChips = ({ segmentler }) => {
  if (!segmentler || segmentler.length === 0) return null;
  return (
    <div className="icra-seg-chips">
      {segmentler.map((s, i) => (
        <span className="icra-seg-chip" key={i}>
          %{s.oran} · {s.gun} gün · {formatCurrency(s.faiz)}
        </span>
      ))}
    </div>
  );
};

// Tek satır: sol tik kutusu + gövde (etiket / tutar + ek içerik)
const Kalem = ({ dahil, onToggle, label, value, sub, children }) => (
  <div className={`icra-row ${dahil ? '' : 'pasif'}`}>
    <input type="checkbox" className="icra-cb" checked={dahil} onChange={onToggle} />
    <div className="icra-row-body">
      <div className="icra-row-line">
        <span className="icra-row-label">{label}{sub && <small>{sub}</small>}</span>
        <span className="icra-row-value">{formatCurrency(value)}</span>
      </div>
      {children}
    </div>
  </div>
);

const IcraHesap = ({ caseFile, onUpdated }) => {
  const cf = caseFile || {};
  const caseFileId = parseId(get(cf, 'Id', 'id'));
  const isSirket = !!get(cf, 'IsSirket', 'isSirket');

  const [takipAcilisMiktari, setTakipAcilisMiktari] = useState('');
  const [takipTarihi, setTakipTarihi] = useState('');
  const [faizTuru, setFaizTuru] = useState('Yasal');
  const [faizOrani, setFaizOrani] = useState('');
  const [avansOranManuel, setAvansOranManuel] = useState(false);
  const [faizBaslangic, setFaizBaslangic] = useState('');
  const [tahsilAsama, setTahsilAsama] = useState('once');
  const [vekaletManuel, setVekaletManuel] = useState('');

  const [borcluSayisi, setBorcluSayisi] = useState(1);
  const [ilamsiz, setIlamsiz] = useState(true);
  const [masrafOverride, setMasrafOverride] = useState('');
  // Dosyaya girilmiş masraf kayıtları — takip açılış masrafı buradan okunur.
  const [masrafKayitlari, setMasrafKayitlari] = useState([]);
  const [ucdeCeyrek, setUcdeCeyrek] = useState(false);
  const [masrafKalemDahil, setMasrafKalemDahil] = useState({ ...DEFAULT_MASRAF_KALEM_DAHIL });

  const [dahil, setDahil] = useState({
    asil: true,
    gecmisFaiz: true,
    isleyenFaiz: true,
    vekalet: true,
    masraf: true,
    tahsilHarci: true,
    cezaeviHarci: false
  });
  const toggle = (k) => setDahil((p) => ({ ...p, [k]: !p[k] }));
  const toggleMasrafKalem = (key) =>
    setMasrafKalemDahil((p) => ({ ...p, [key]: !p[key] }));

  const [bugun, setBugun] = useState(bugunISO());
  const [saving, setSaving] = useState(false);
  const [durum, setDurum] = useState(null);

  // İşleyen faiz her gün güncellensin (açık sayfada da)
  useEffect(() => {
    const sync = () => setBugun(bugunISO());
    sync();
    const id = setInterval(sync, 60 * 1000);
    const onFocus = () => sync();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Takip açılış masrafı artık hesaplanmıyor, dosyanın masraf kayıtlarından
  // okunuyor. Sebebi: otomatik hesap UYAP'ın verdiği tutarı tutturamıyordu.
  useEffect(() => {
    let iptal = false;
    (async () => {
      if (!caseFileId) return;
      try {
        const liste = await masrafAPI.getByCaseFileId(caseFileId);
        if (!iptal) setMasrafKayitlari(Array.isArray(liste) ? liste : []);
      } catch (e) {
        if (!iptal) setMasrafKayitlari([]);
      }
    })();
    return () => { iptal = true; };
  }, [caseFileId]);

  useEffect(() => {
    setTakipAcilisMiktari(String(get(cf, 'TakipAcilisMiktari', 'takipAcilisMiktari') ?? '') || '');
    setTakipTarihi(toDateInput(get(cf, 'TakipTarihi', 'takipTarihi')));
    const loadedTur = get(cf, 'IcraFaizTuru', 'icraFaizTuru') || 'Yasal';
    const loadedOran = String(get(cf, 'IcraFaizOrani', 'icraFaizOrani') ?? '') || '';
    const autoAvans = String(getGuncelAvansOrani());
    setFaizTuru(loadedTur);
    setFaizOrani(loadedTur === 'Avans' && !loadedOran ? autoAvans : loadedOran);
    setFaizBaslangic(toDateInput(get(cf, 'IcraFaizBaslangicTarihi', 'icraFaizBaslangicTarihi')));
    setTahsilAsama(get(cf, 'IcraTahsilAsamasi', 'icraTahsilAsamasi') || 'once');
    setVekaletManuel(String(get(cf, 'IcraVekaletUcretiManuel', 'icraVekaletUcretiManuel') ?? '') || '');

    // Onay kutuları / ayarlar (JSON)
    const ayarStr = get(cf, 'IcraHesapAyar', 'icraHesapAyar');
    if (ayarStr) {
      try {
        const a = JSON.parse(ayarStr);
        if (typeof a.ucdeCeyrek === 'boolean') setUcdeCeyrek(a.ucdeCeyrek);
        if (a.dahil && typeof a.dahil === 'object') {
          setDahil((p) => ({ ...p, ...a.dahil }));
        }
        if (a.borcluSayisi != null) setBorcluSayisi(Math.max(1, parseInt(a.borcluSayisi) || 1));
        if (typeof a.ilamsiz === 'boolean') setIlamsiz(a.ilamsiz);
        if (a.masrafOverride != null) setMasrafOverride(String(a.masrafOverride));
        if (a.masrafKalemDahil && typeof a.masrafKalemDahil === 'object') {
          setMasrafKalemDahil((p) => {
            const merged = { ...p, ...a.masrafKalemDahil };
            if (a.masrafKalemSurum == null || a.masrafKalemSurum < 5) {
              merged.basvurma = true;
              merged.pesin = true;
              merged.suret = true;
              merged.baro = true;
              merged.tebligat = true;
              merged.etebligat = false;
              merged.dosya = false;
            }
            return merged;
          });
        } else if (a.masrafKalemSurum == null || a.masrafKalemSurum < 5) {
          setMasrafKalemDahil({ ...DEFAULT_MASRAF_KALEM_DAHIL });
        }
        // v7: Asıl yazılır, geçmiş faiz otomatik; kesinleşen = asıl + geçmiş. Faize faiz yok.
        if (a.masrafKalemSurum == null || a.masrafKalemSurum < 7) {
          setDahil((p) => ({ ...p, gecmisFaiz: true }));
        }
        const inferredManuel = loadedTur === 'Avans'
          && loadedOran !== ''
          && a.avansOranManuel !== false
          && !isVarsayilanAvansOrani(loadedOran);
        setAvansOranManuel(a.avansOranManuel === true || inferredManuel);
      } catch (e) {
        setAvansOranManuel(
          loadedTur === 'Avans' && loadedOran !== '' && !isVarsayilanAvansOrani(loadedOran)
        );
      }
    } else {
      setAvansOranManuel(
        loadedTur === 'Avans' && loadedOran !== '' && !isVarsayilanAvansOrani(loadedOran)
      );
    }
  }, [
    caseFileId,
    get(cf, 'TakipAcilisMiktari', 'takipAcilisMiktari'),
    get(cf, 'TakipTarihi', 'takipTarihi'),
    get(cf, 'IcraFaizTuru', 'icraFaizTuru'),
    get(cf, 'IcraFaizOrani', 'icraFaizOrani'),
    get(cf, 'IcraFaizBaslangicTarihi', 'icraFaizBaslangicTarihi'),
    get(cf, 'IcraTahsilAsamasi', 'icraTahsilAsamasi'),
    get(cf, 'IcraVekaletUcretiManuel', 'icraVekaletUcretiManuel'),
    get(cf, 'IcraHesapAyar', 'icraHesapAyar'),
  ]);

  useEffect(() => {
    if (faizTuru !== 'Avans' || avansOranManuel) return;
    const auto = String(getGuncelAvansOrani());
    setFaizOrani((prev) => (prev === auto ? prev : auto));
  }, [faizTuru, avansOranManuel]);

  const girildi = parseTutar(takipAcilisMiktari) > 0;
  const avansTabloKullan = faizTuru === 'Avans' && !avansOranManuel;

  const pesinMatrah = useMemo(() => {
    const asil = parseTutar(takipAcilisMiktari);
    if (!girildi) return 0;
    if (!faizBaslangic || !takipTarihi) return asil;
    const detay = hesaplaFaiz(asil, faizBaslangic, takipTarihi, faizTuru, Number(faizOrani) || 0, avansTabloKullan ? false : avansOranManuel);
    return asil + (Number(detay?.faiz) || 0);
  }, [girildi, takipAcilisMiktari, faizBaslangic, takipTarihi, faizTuru, faizOrani, avansOranManuel, avansTabloKullan]);

  const masrafAuto = useMemo(
    () =>
      girildi
        ? icraAcilisMasraflari({
          takipMiktari: parseTutar(takipAcilisMiktari),
          pesinMatrah,
          borcluSayisi,
          ilamsiz,
          isSirket
        })
        : { kalemler: [], toplam: 0 },
    [girildi, takipAcilisMiktari, pesinMatrah, borcluSayisi, ilamsiz, isSirket]
  );
  /** Dosyaya girilmiş icra açılış masrafları (gider kayıtları). */
  const acilisKayitlari = useMemo(
    () => (masrafKayitlari || []).filter(
      (m) => !(m.IsGelir ?? m.isGelir) && acilisMasrafiMi(m.MasrafTuru ?? m.masrafTuru)
    ),
    [masrafKayitlari]
  );

  const masrafKayitToplam = useMemo(
    () => acilisKayitlari.reduce((t, m) => t + (Number(m.Tutar ?? m.tutar) || 0), 0),
    [acilisKayitlari]
  );

  /**
   * Masraf tutarının kaynağı, öncelik sırasıyla:
   *   1. Elle yazılan tutar (her şeyi ezer)
   *   2. Dosyanın masraf kayıtlarındaki icra açılış masrafları
   *   3. Otomatik hesap (yalnızca kayıt yoksa — UYAP ile birebir tutmuyor)
   */
  const masrafKaynagi = !girildi
    ? 'yok'
    : masrafOverride !== '' ? 'elle'
      : masrafKayitToplam > 0 ? 'masraf'
        : 'otomatik';

  const masrafKullanilan =
    masrafKaynagi === 'elle' ? (Number(masrafOverride) || 0)
      : masrafKaynagi === 'masraf' ? masrafKayitToplam
        : masrafKaynagi === 'otomatik' ? masrafToplamFromKalemler(masrafAuto.kalemler, masrafKalemDahil)
          : 0;

  const sonuc = useMemo(
    () =>
      hesaplaIcra({
        asilAlacak: Number(takipAcilisMiktari) || 0,
        faizBaslangic,
        takipTarihi,
        faizTuru,
        faizOrani: Number(faizOrani) || 0,
        oranManuel: avansTabloKullan ? false : avansOranManuel,
        masrafToplam: masrafKullanilan,
        tahsilAsama,
        vekaletManuel: vekaletManuel === '' ? null : vekaletManuel,
        ucdeCeyrek,
        kesinlesenUyap: null,
        faizTakibeDahil: false,
        bugun
      }),
    [takipAcilisMiktari, faizBaslangic, takipTarihi, faizTuru, faizOrani, avansOranManuel, avansTabloKullan, masrafKullanilan, tahsilAsama, vekaletManuel, ucdeCeyrek, bugun]
  );

  const uyapMasrafMiktari = masrafAuto.kalemler.length
    ? uyapDosyaHesabiMasraf(masrafAuto.kalemler)
    : 836;

  const uyapToplam =
    sonuc.kesinlesen +
    (dahil.isleyenFaiz ? sonuc.isleyenFaiz : 0) +
    (dahil.vekalet ? sonuc.vekalet : 0) +
    uyapMasrafMiktari +
    (dahil.tahsilHarci ? sonuc.tahsilHarci : 0);

  const toplamAlacak = uyapToplam + (dahil.cezaeviHarci ? sonuc.cezaeviHarci : 0);

  const eksik = !(Number(takipAcilisMiktari) > 0);
  const showFaizOrani = faizTuru === 'Avans' || faizTuru === 'Sozlesme';
  const maktu = sonuc.kesinlesen <= 56250;

  const handleKaydet = async () => {
    if (!caseFileId) {
      setDurum({ tip: 'err', mesaj: 'Dosya numarası bulunamadı' });
      return;
    }
    setSaving(true);
    setDurum(null);
    try {
      const updated = await caseFileAPI.updateIcraBilgileri(caseFileId, {
        TakipAcilisMiktari: numOrNull(takipAcilisMiktari),
        // Diğer tarih alanlarıyla aynı: İstanbul günü → UTC ISO (1 gün kayması olmasın)
        TakipTarihi: toISODateOrNull(takipTarihi),
        IcraFaizTuru: faizTuru || null,
        IcraFaizOrani: showFaizOrani ? numOrNull(faizOrani) : null,
        IcraFaizBaslangicTarihi: toISODateOrNull(faizBaslangic),
        IcraTahsilAsamasi: tahsilAsama || null,
        IcraVekaletUcretiManuel: numOrNull(vekaletManuel),
        IcraHesapAyar: JSON.stringify({ ucdeCeyrek, dahil, borcluSayisi, ilamsiz, masrafOverride, masrafKalemDahil, masrafKalemSurum: MASRAF_KALEM_SURUM, avansOranManuel, faizTakibeDahil: false })
      });

      // Tutar zaten masraf kayıtlarından geliyorsa geri yazmıyoruz; yoksa
      // kullanıcının girdiği kaydın üzerine kendi hesabımızı yazmış oluruz.
      if (dahil.masraf && masrafKaynagi !== 'masraf' && Number(masrafKullanilan) > 0) {
        try {
          const liste = await masrafAPI.getByCaseFileId(caseFileId);
          const mevcut = (liste || []).find((m) => {
            const t = String(m.MasrafTuru || m.masrafTuru || '').trim();
            return t === ICRA_ACILIS_MASRAF_TURU;
          });
          const tarihISO = toISODateOrNull(takipTarihi) || toISODateOrNull(bugun);
          const payload = {
            CaseFileId: caseFileId,
            MasrafTuru: ICRA_ACILIS_MASRAF_TURU,
            Tutar: Math.round(Number(masrafKullanilan) * 100) / 100,
            IsGelir: false,
            Tarih: tarihISO,
            MasrafIstenmeTarihi: tarihISO,
            SonTarih: tarihISO,
            Aciklama: 'İcra hesabından otomatik — takip açılış masrafları'
          };
          if (mevcut) {
            await masrafAPI.update({ ...payload, Id: mevcut.Id ?? mevcut.id });
          } else {
            await masrafAPI.create(payload);
          }
        } catch (masrafErr) {
          console.error('İcra açılış masrafı kaydı:', masrafErr);
        }
      }

      setDurum({ tip: 'ok', mesaj: 'Kaydedildi' });
      try {
        if (onUpdated && updated) onUpdated(updated);
      } catch (cbErr) {
        console.error('İcra kaydı sonrası yenileme:', cbErr);
      }
    } catch (e) {
      console.error('İcra hesabı kaydı:', e);
      setDurum({ tip: 'err', mesaj: apiHataMesaji(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="icra-hesap">
      {/* Başlık + kaydet */}
      <div className="icra-hesap-header">
        <div className="icra-head-left">
          <h3 className="icra-hesap-title">İcra Hesabı</h3>
          <span className="icra-hesap-today">{formatTR(bugun)} itibarıyla canlı</span>
        </div>
        <div className="icra-head-actions">
          {durum && <span className={`icra-save-msg ${durum.tip}`}>{durum.mesaj}</span>}
          <button className="btn btn-primary btn-sm" onClick={handleKaydet} disabled={saving || !caseFileId}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      {/* Girişler */}
      <section className="icra-card">
        <div className="icra-card-title">Takip Bilgileri</div>
        <div className="icra-input-grid">
          <div className="icra-field">
            <label>Asıl Alacak (₺)</label>
            {/* Metin girdisi: sayı girdisi "12.000"i 12 olarak okuyordu. */}
            <input
              type="text"
              inputMode="decimal"
              value={takipAcilisMiktari}
              onChange={(e) => setTakipAcilisMiktari(e.target.value)}
              placeholder="Örn. 12.000 veya 160421,11"
            />
            <p className="icra-field-hint">Yalnız asıl alacağı yazın. Geçmiş gün faizi faiz başlangıç tarihinden otomatik hesaplanır; UYAP kesinleşen = asıl + geçmiş faiz.</p>
          </div>
          <div className="icra-field">
            <label>Takip Tarihi</label>
            <input type="date" value={takipTarihi} onChange={(e) => setTakipTarihi(e.target.value)} />
          </div>
          <div className="icra-field">
            <label>Faiz Türü</label>
            <select
              value={faizTuru}
              onChange={(e) => {
                const next = e.target.value;
                setFaizTuru(next);
                if (next === 'Avans') {
                  setAvansOranManuel(false);
                  setFaizOrani(String(getGuncelAvansOrani()));
                }
              }}
            >
              <option value="Yasal">Yasal (Adi Kanuni) Faiz</option>
              <option value="Avans">Avans (Reeskont) Faizi</option>
              <option value="Sozlesme">Sözleşme (Akdi) Faizi</option>
            </select>
          </div>
          <div className="icra-field">
            <label>Faiz Başlangıç (Temerrüt) Tarihi</label>
            <input type="date" value={faizBaslangic} onChange={(e) => setFaizBaslangic(e.target.value)} />
          </div>
          {showFaizOrani && (
            <div className="icra-field">
              <label>Yıllık Faiz Oranı (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={faizOrani}
                onChange={(e) => {
                  setFaizOrani(e.target.value);
                  if (faizTuru === 'Avans') setAvansOranManuel(true);
                }}
                placeholder={faizTuru === 'Avans' ? String(getGuncelAvansOrani()) : 'Örn. 55'}
              />
              {faizTuru === 'Avans' && (
                <p className="icra-field-hint">
                  {avansOranManuel
                    ? 'Elle girilen sabit oran tüm faiz süresine uygulanır.'
                    : `TCMB reeskont (iskonto) tablosu dönemsel uygulanır (2026 güncel %${getGuncelAvansOrani()}). Eski tarihlerde o dönemin oranı kullanılır.`}
                  {avansOranManuel && (
                    <button
                      type="button"
                      className="icra-link-btn"
                      onClick={() => {
                        setAvansOranManuel(false);
                        setFaizOrani(String(getGuncelAvansOrani()));
                      }}
                    >
                      TCMB tablosuna dön
                    </button>
                  )}
                </p>
              )}
            </div>
          )}
          <div className="icra-field">
            <label>Vekalet Ücreti (elle — boşsa 2026 tarifesi)</label>
            <input
              type="text"
              inputMode="decimal"
              value={vekaletManuel}
              onChange={(e) => setVekaletManuel(e.target.value)}
              placeholder="Otomatik (AAÜT 2026)"
            />
          </div>
        </div>
        <label className="icra-ucdeceyrek">
          <input type="checkbox" checked={ucdeCeyrek} onChange={(e) => setUcdeCeyrek(e.target.checked)} />
          Ödeme emri süresinde ödeme — vekalet ücretinin ¾&apos;ü (AAÜT m.11)
        </label>
      </section>

      {eksik && (
        <div className="icra-hesap-uyari">
          Tam hesap için <b>Asıl Alacak</b>, <b>Takip Tarihi</b> ve <b>Faiz Başlangıç Tarihi</b> girin.
        </div>
      )}

      {girildi && (
        <section className="icra-card icra-uyap-ozet">
          <div className="icra-card-title">
            UYAP Dosya Hesap Bilgileri
            <small>UYAP ekranındaki satırlarla birebir</small>
          </div>
          <div className="icra-uyap-grid">
            <div className="icra-uyap-row">
              <span>
                Takipte Kesinleşen Miktar
                <small>asıl {formatCurrency(sonuc.asil)} + geçmiş faiz {formatCurrency(sonuc.gecmisFaiz)}</small>
              </span>
              <strong>{formatCurrency(sonuc.kesinlesen)}</strong>
            </div>
            <div className="icra-uyap-row">
              <span>Toplam Faiz Miktarı</span>
              <strong>{formatCurrency(dahil.isleyenFaiz ? sonuc.isleyenFaiz : 0)}</strong>
            </div>
            <div className="icra-uyap-row">
              <span>Vekalet Ücreti{ucdeCeyrek ? ' (¾)' : ''}</span>
              <strong>{formatCurrency(dahil.vekalet ? sonuc.vekalet : 0)}</strong>
            </div>
            <div className="icra-uyap-row">
              <span>Masraf Miktarı</span>
              <strong>{formatCurrency(uyapMasrafMiktari)}</strong>
            </div>
            <div className="icra-uyap-row">
              <span>Tahsil Harcı</span>
              <strong>{formatCurrency(dahil.tahsilHarci ? sonuc.tahsilHarci : 0)}</strong>
            </div>
            <div className="icra-uyap-row icra-uyap-total">
              <span>Toplam Alacak</span>
              <strong>{formatCurrency(uyapToplam)}</strong>
            </div>
          </div>
          <div className="icra-uyap-row" style={{ marginTop: 12, borderTop: '1px dashed #e2e8f0', paddingTop: 10 }}>
            <span>Takip açılış masrafı <small>(dosyaya işlenecek)</small></span>
            <strong>{formatCurrency(masrafKullanilan)}</strong>
          </div>
          <p className="icra-masraf-hint" style={{ marginTop: 8 }}>
            Kesinleşen = asıl + geçmiş gün faizi (işleyen faiz yalnız asıl üzerinden, faize faiz yok).
            UYAP Masraf Miktarı = başvurma + suret (732 + 104 = 836 ₺).
            Dosyaya işlenen açılış = 732 + peşin (kesinleşenin ‰5 ={' '}
            {formatCurrency(masrafAuto.kalemler.find((k) => k.key === 'pesin')?.tutar || 0)})
            + 104 + 164 + 265.
          </p>
        </section>
      )}

      {/* Özet */}
      <section className="icra-card">
        <div className="icra-card-title">
          Hesap Özeti
          <small>Asıl + geçmiş = kesinleşen. İşleyen / vekalet / tahsil tikini kaldırırsanız UYAP toplamına girmez.</small>
        </div>

        <div className="icra-hesap-rows">
          <Kalem dahil={dahil.asil} onToggle={() => toggle('asil')} label="Asıl Alacak" value={sonuc.asil} />

          <Kalem
            dahil={dahil.gecmisFaiz}
            onToggle={() => toggle('gecmisFaiz')}
            label="Geçmiş Gün Faizi"
            sub={`${faizBaslangic ? formatTR(faizBaslangic) : '-'} → ${takipTarihi ? formatTR(takipTarihi) : 'takip tarihi girilmedi'} · kesinleşene eklenir, işleyen faiz yalnız asıla`}
            value={sonuc.gecmisFaiz}
          >
            <SegmentChips segmentler={sonuc.gecmisFaizDetay.segmentler} />
          </Kalem>

          <Kalem
            dahil={dahil.isleyenFaiz}
            onToggle={() => toggle('isleyenFaiz')}
            label="İşleyen Faiz (bugüne kadar)"
            sub={`${takipTarihi ? formatTR(takipTarihi) : (faizBaslangic ? formatTR(faizBaslangic) : '-')} → ${formatTR(bugun)} · yalnız asıl alacak`}
            value={sonuc.isleyenFaiz}
          >
            <SegmentChips segmentler={sonuc.isleyenFaizDetay.segmentler} />
          </Kalem>

          <Kalem
            dahil={dahil.vekalet}
            onToggle={() => toggle('vekalet')}
            label="Vekalet Ücreti"
            sub={sonuc.vekaletManuelKullanildi ? 'elle girildi' : `AAÜT 2026 · ${maktu ? 'maktu' : 'nispi'}${ucdeCeyrek ? ' · ¾' : ''}`}
            value={sonuc.vekalet}
          />

          <Kalem
            dahil={dahil.masraf}
            onToggle={() => toggle('masraf')}
            label="İcra Açılış Masrafları (2026)"
            value={masrafKullanilan}
          >
            <details className="icra-details">
              <summary>
                {masrafKaynagi === 'masraf' ? 'Masraf kayıtlarından alındı' : 'Kalemler ve ayarlar'}
              </summary>

              {masrafKaynagi === 'masraf' && (
                <div className="icra-masraf-kaynak">
                  <p className="icra-masraf-kaynak-not">
                    Bu tutar dosyanın masraf kayıtlarından okundu, hesaplanmadı.
                    Değiştirmek için masraf kaydını düzeltin ya da aşağıya elle bir tutar yazın.
                  </p>
                  <ul className="icra-masraf-kaynak-liste">
                    {acilisKayitlari.map((m) => (
                      <li key={m.Id ?? m.id}>
                        <span>{m.MasrafTuru ?? m.masrafTuru}</span>
                        <span>{formatCurrency(Number(m.Tutar ?? m.tutar) || 0)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {masrafKaynagi === 'otomatik' && (
                <p className="icra-masraf-kaynak-not">
                  Dosyada icra açılış masrafı kaydı yok; aşağıdaki tutar hesaplanmıştır.
                  UYAP ile birebir tutmayabilir — kesin tutar için masraf kısmına
                  &quot;İcra Takip Açılış&quot; kaydı girin, buraya oradan gelsin.
                </p>
              )}

              <div className="icra-masraf-kalemler">
                {masrafAuto.kalemler.map((k) => (
                  <label className="icra-masraf-kalem" key={k.key || k.ad}>
                    <input
                      type="checkbox"
                      checked={masrafKalemDahil[k.key] !== false}
                      onChange={() => toggleMasrafKalem(k.key)}
                      disabled={masrafKaynagi !== 'otomatik'}
                    />
                    <span>{k.ad}</span>
                    <span>{formatCurrency(k.tutar)}</span>
                  </label>
                ))}
              </div>
              <p className="icra-masraf-hint">
                Varsayılan kalemler UYAP takip masrafı toplamı ile aynıdır (2026: başvurma + peşin + suret + baro + 265 ₺ avans).
                Peşin harç, takipte kesinleşen miktarın binde 5&apos;idir.
              </p>
              <div className="icra-masraf-ayar">
                <label className="icra-masraf-ayar-item">
                  Borçlu sayısı
                  <input type="number" min="1" value={borcluSayisi} onChange={(e) => setBorcluSayisi(Math.max(1, parseInt(e.target.value) || 1))} />
                </label>
                <label className="icra-masraf-ayar-item icra-check">
                  <input type="checkbox" checked={ilamsiz} onChange={(e) => setIlamsiz(e.target.checked)} />
                  İlamsız takip (peşin harç)
                </label>
                <label className="icra-masraf-ayar-item">
                  Elle masraf (₺)
                  <input type="number" step="0.01" min="0" value={masrafOverride} onChange={(e) => setMasrafOverride(e.target.value)} placeholder="otomatik" />
                </label>
              </div>
            </details>
          </Kalem>

          <Kalem
            dahil={dahil.tahsilHarci}
            onToggle={() => toggle('tahsilHarci')}
            label="Tahsil Harcı"
            sub="kesinleşen miktar üzerinden"
            value={sonuc.tahsilHarci}
          >
            <select className="icra-inline-select" value={tahsilAsama} onChange={(e) => setTahsilAsama(e.target.value)}>
              {Object.entries(TAHSIL_ASAMA_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </Kalem>

          <Kalem
            dahil={dahil.cezaeviHarci}
            onToggle={() => toggle('cezaeviHarci')}
            label="Cezaevi Harcı (%2)"
            sub="tahsilatta alacaklıdan kesilir"
            value={sonuc.cezaeviHarci}
          />
        </div>
      </section>

      {/* Toplam */}
      <div className="icra-hesap-total">
        <span className="icra-total-label">TOPLAM ALACAK</span>
        <span className="icra-total-value">{formatCurrency(uyapToplam)}</span>
      </div>

      <p className="icra-hesap-disclaimer">
        UYAP Dosya Hesabı: kesinleşen (asıl + geçmiş faiz) + işleyen faiz (yalnız asıl) + vekalet + masraf (836) + tahsil.
        Geçmiş faize tekrar faiz işlemez. Kaydet, açılış masrafını dosyaya işler.
      </p>
    </div>
  );
};

export default IcraHesap;
